import { AbstractUrlService } from './abstractUrlService';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ContractNumberOrId, FilterValues, InvoicingDateRange } from './admin-contract.service';
import { Contract } from '../models/contract';
import { delay, firstValueFrom, Observable, of } from 'rxjs';
import _ from 'lodash';
import { buildHttpParams } from '@shared/utils/request-utils';
import moment from 'moment';
import { Config } from 'config/config';

export type InvoicingReportRequest = InvoicingDateRange & InvoicingFilters;

export type InvoicingReport = TimedBucket[];
export type TimedBucket = {
  date: string;
  totalRequests: number;
  invoicedRequests: number;
  bandwidthIn: number;
  bandwidthOut: number;
  bandwidthTotal: number;
  nbSites: number;
  nbAdvancedSites: number;
  nbExpertSites: number;
};

/**
 * AbstractContractService and OrganizationContractService should not be in the same file
 * But otherwise there's a strange error at runtime :
 * Uncaught TypeError: class heritage AbstractContractService is not an object or null
 */

export abstract class AbstractContractService<T> extends AbstractUrlService {
  protected constructor(
    private readonly config: Config,
    protected readonly http: HttpClient,
    getBaseUrl: () => string,
  ) {
    super(getBaseUrl);
  }

  async getContract(contractRef: ContractNumberOrId): Promise<Contract> {
    return firstValueFrom(this.http.get<Contract>(this.url(`/contracts/${contractRef}`)));
  }

  getInvoicingStats(contractNumbers: string[]): Observable<{ [k: string]: T }> {
    if (this.config.mockAccountingData) {
      return of(
        _(contractNumbers)
          .map((contractNumber) => {
            const sample = sampleBucket(1);
            return [
              contractNumber,
              <T>{
                currentMonth: sample,
                nbTotalSites: sample.nbSites + 2,
                nbInvoicedSites: sample.nbSites,
                nbInvoicedClusters: 0,
                nbOrganizations: 2,
              },
            ];
          })
          .fromPairs()
          .value(),
      );
    } else {
      return contractNumbers.length > 0
        ? this.http.get<{ [k: string]: T }>(this.url('contracts/invoicing-stats'), {
            params: buildHttpParams({ contractNumbers: contractNumbers }),
          })
        : of({});
    }
  }

  getInvoicingReport(
    contractIdOrNumber: ContractNumberOrId,
    request: InvoicingReportRequest,
  ): Observable<InvoicingReport> {
    if (this.config.mockAccountingData) {
      return of(Array.from({ length: request.nbPeriods }, (v, i) => i - request.nbPeriods + 1).map(sampleBucket)).pipe(
        delay(300),
      );
    } else {
      return this.http.get<InvoicingReport>(this.url(`contracts/${contractIdOrNumber}/invoicing-report`), {
        params: buildHttpParams(request),
      });
    }
  }

  getFilterValues(contractIdOrNumber: ContractNumberOrId, request: InvoicingDateRange): Observable<FilterValues> {
    if (this.config.mockAccountingData) {
      return of({
        organizations: [
          {
            name: 'Ogo',
            code: 'ogo-123',
          },
        ],
        clusters: [
          {
            name: '.Ogo EU W1',
            uid: '7f7fb410-f5e4-47a4-bca2-aa4e451d1b95',
          },
        ],
        tags: ['test', 'dashboard'],
      }).pipe(delay(100));
    } else {
      const params: HttpParams = buildHttpParams(request);
      return this.http.get<FilterValues>(this.url(`contracts/${contractIdOrNumber}/filter-values`), { params });
    }
  }
}
// BEGIN-NOSCAN
function sampleBucket(monthAgo: number) {
  if (Math.random() > 0.9) {
    return {
      date: moment().utc().startOf('month').add(monthAgo, 'month').toISOString(),
      bandwidthIn: 0,
      bandwidthOut: 0,
      bandwidthTotal: 0,
      invoicedRequests: 0,
      totalRequests: 0,
      nbAdvancedSites: 0,
      nbExpertSites: 0,
      nbSites: 0,
    };
  }
  const invoicedRequests = randomInt(5000000, 9000000);
  const bandwidthIn = randomInt(800000000, 1200000000);
  const bandwidthOut = randomInt(40000000000, 60000000000);
  return {
    date: moment().utc().startOf('month').add(monthAgo, 'month').toISOString(),
    bandwidthIn: bandwidthIn,
    bandwidthOut: bandwidthOut,
    bandwidthTotal: bandwidthIn + bandwidthOut,
    invoicedRequests: invoicedRequests,
    totalRequests: invoicedRequests + randomInt(100000, 1000000),
    nbAdvancedSites: randomInt(8, 12),
    nbExpertSites: randomInt(0, 6),
    nbSites: randomInt(25, 35),
  };
}
// END-NOSCAN

function randomInt(min: number, max: number) {
  return min + Math.round(Math.random() * (max + 1 - min));
}

export type InvoicingFilters = { organizationCodes: string; clusterUids?: string; tags: string };
